import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import OrderSummaryBox from './OrderSummaryBox';
import { fetchCalculateInfo } from '../../../actions/tools/CommonOrderActions';
import { v4 as uuid } from 'uuid';
import debounce from 'lodash/debounce';
import * as Styled from '../common/CommonStyledComponents';
import Spinner from '../../common/spinner';
import { getSelectedOrder } from '../../../selectors/tools';

const uniqueRequestUuid = uuid();

class OrderSummaryBoxFromServer extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    currencyCode: PropTypes.string.isRequired,
    refundItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        quantity: PropTypes.number,
      })
    ),
  };

  onChange(keysToUpdate = []) {
    return () => {
      const { onChange } = this.props;
      if (typeof onChange === 'function') {
        const state = keysToUpdate.reduce((acc, key) => {
          acc[key] = this.state[key];

          return acc;
        }, {});

        onChange(state);
      }
    };
  }

  constructor(props) {
    super(props);
    const { currencyCode } = this.props;
    this._isMounted = false;
    this.state = {
      currencyCode: currencyCode,
      request: true,
      error: false,
      totals: {},
    };

    this.debounceUpdateAmountsFromServer = debounce(this.updateAmountsFromServer.bind(this), 1000);
  }

  async componentDidMount(props) {
    this._isMounted = true;
    this.onChange(['request'])();
    await this.updateAmountsFromServer();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  get orderId() {
    const { selectedOrder } = this.props;

    return String(selectedOrder.increment_id || selectedOrder.id);
  }

  shouldUpdateTotals(prevProps, newProps) {
    const { refundItems: prevRefundItems } = prevProps;
    const { refundItems: newRefundItems } = newProps;

    if (prevRefundItems.length !== newRefundItems.length) {
      return true;
    }

    return newRefundItems
      .map((newItem) => {
        const prevItem = prevRefundItems.find((item) => item['id'] === newItem['id']);

        return !prevItem || prevItem.quantity !== newItem.quantity;
      })
      .some((elem) => elem);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.refundItems && this.shouldUpdateTotals(prevProps, this.props)) {
      this.setState({ request: true, error: false }, this.onChange(['request']));
      this.debounceUpdateAmountsFromServer();
    }
  }

  async updateAmountsFromServer() {
    try {
      const totals = await this.props.fetchCalculateInfo({
        orderId: this.orderId,
        refundItems: this.props.refundItems,
        uniqueRequestUuid,
      });
      if (this._isMounted) {
        this.setState({ totals, request: false }, this.onChange(['request', 'totals']));
      }
    } catch (err) {
      if (err.message !== 'Aborted') {
        if (this._isMounted) {
          this.setState({
            request: false,
            error: true,
          });
        }
      }
    }
  }

  render() {
    if (this.state.request) {
      return (
        <Styled.OrderSummaryBox>
          <Styled.OrderSummaryInfo>
            <Styled.LoadingBlock>
              <Spinner size={20} />
            </Styled.LoadingBlock>
          </Styled.OrderSummaryInfo>
        </Styled.OrderSummaryBox>
      );
    }

    if (this.state.error) {
      return (
        <Styled.OrderSummaryBox>
          <Styled.OrderSummaryInfo>
            <Styled.ErrorBlock>Something went wrong..</Styled.ErrorBlock>
          </Styled.OrderSummaryInfo>
        </Styled.OrderSummaryBox>
      );
    }

    return <OrderSummaryBox {...this.state.totals} currencyCode={this.props.currencyCode} />;
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrder(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchCalculateInfo,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummaryBoxFromServer);
